/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect
} from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useApiRequest, useApi, useNotifications } from '../../hooks'
import { baseURL } from '../../api'
import { IconPickerItem } from '../Form/IconField'
import ToggleButton from '../ToggleButton'
// import Slider from '../Slider'
import { useMapSettings } from '../Dispatch/DispatchMap'

import mapLayerRoads from './ic_default-2x.png'
import mapLayerSatellite from './ic_satellite-2x.png'
import mapLayerTerrain from './ic_terrain-2x.png'
import mapLayerTraffic from './ic_traffic-2x.png'

import './styles.scss'
import { signOut } from 'firebase/auth'
import { auth, db } from '../../firebase'
import { ApiContext } from '../../hooks/api'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faLayerGroup } from '@fortawesome/free-solid-svg-icons'

function NavLink({ to, external, match, style, children, ...props }) {
  const pathname = useLocation().pathname
  const LinkClass = external ? 'a' : Link

  return (
    <LinkClass
      href={external && to}
      to={to}
      className={`navbar-item ${pathname.startsWith(match) ? 'is-active' : ''}`}
      {...props}>
      {children}
    </LinkClass>
  )
}

export default function () {
  const [isActive, setActive] = useState(false)
  const isDispatch = useLocation().pathname === '/dispatch'
  const { user } = useContext(ApiContext)
  const [localUser, setLocalUser] = useState(user)
  const [profile, reloadProfile] = useApiRequest(`users/${user?.id}`)
  // const isAdmin = profile && profile.organization.id === 1
  const isAdmin =
    profile &&
    profile?.organization.mapValue.fields.id.integerValue === '202509081156'
  const [chatNotifications, setChatNotifications] = useState(
    user?.chatNotification || false
  )
  const [showLastName, setShowLastName] = useState(user?.showLastName || false)
  const [client] = useApi()
  const addNotification = useNotifications()
  const [mapSettings, setMapSettings] = useMapSettings()
  const [activeSubMenu, setActiveSubMenu] = useState()

  const updateChatNotifications = useCallback(
    async (value) => {
      try {
        await client(
          `users/${user?.id}?updateMask.fieldPaths=chatNotification`,
          {
            fields: {
              chatNotification: { booleanValue: value }
            }
          },
          'PATCH'
        )

        setLocalUser((prev) => ({
          ...prev,
          chatNotification: value
        }))
        setChatNotifications(value)

        addNotification(<div>Chat notifications updated successfully.</div>, {
          className: 'is-success'
        })
      } catch (err) {
        console.error(
          'Chat notification update error:',
          err.response?.data || err
        )
        addNotification(
          <div>
            Failed to update chat notifications. Check console for details.
          </div>,
          { className: 'is-danger' }
        )
      }
    },
    [client, user?.id, addNotification]
  )

  const updateShowLastName = useCallback(
    async (value) => {
      try {
        // 1. Update Firebase
        await client(
          `users/${user?.id}?updateMask.fieldPaths=showLastName`,
          {
            fields: {
              showLastName: { booleanValue: value }
            }
          },
          'PATCH'
        )

        // 2. Update local user state
        setLocalUser((prev) => ({
          ...prev,
          showLastName: value
        }))
        setShowLastName(value)

        // 3. Update map settings (this is the existing functionality you want to keep)
        setMapSettings({ ...mapSettings, showLastName: value })

        addNotification(
          <div>Last name display preference updated successfully.</div>,
          { className: 'is-success' }
        )
      } catch (err) {
        console.error('Show last name update error:', err.response?.data || err)
        addNotification(
          <div>
            Failed to update last name preference. Check console for details.
          </div>,
          { className: 'is-danger' }
        )
      }
    },
    [client, user?.id, addNotification, mapSettings, setMapSettings]
  )

  useEffect(() => {
    setLocalUser(user)
  }, user)

  const displayName = useMemo(
    () =>
      profile &&
      Array.isArray(profile.personalData) &&
      profile.personalData.find((p) => p.type?.typeName === 'displayName')
        ?.value,
    [profile]
  )

  const [dutyStatusLoading, setDutyStatusLoading] = useState(false)

  const normalizeDutyStatus = (status) => {
    if (status === undefined || status === null) return false
    if (typeof status === 'boolean') return status
    if (typeof status === 'string') return status === 'on-duty'
    return false
  }

  const setDutyStatus = useCallback(
    async (value) => {
      setDutyStatusLoading(true)
      try {
        // Convert boolean to string for API consistency
        const newDutyStatus = value ? 'on-duty' : 'off-duty'
        const newDutyStatusBoolean = value // Keep boolean for Firebase

        // Update API
        await client(
          `users/${user?.id}?updateMask.fieldPaths=dutyStatus`,
          {
            fields: {
              dutyStatus: { stringValue: newDutyStatus }
            }
          },
          'PATCH'
        )

        // Update local user state
        setLocalUser((prevUser) => ({
          ...prevUser,
          dutyStatus: newDutyStatus,
          dutyStatusBoolean: newDutyStatusBoolean
        }))

        // Update Firebase
        const userId = auth.currentUser?.uid
        if (userId) {
          const userRef = doc(db, 'users', userId)
          await setDoc(
            userRef,
            {
              dutyStatus: newDutyStatusBoolean,
              lastActive: new Date().toISOString()
            },
            { merge: true }
          )
        }

        await reloadProfile()

        addNotification(<div>Duty status updated successfully.</div>, {
          className: 'is-success'
        })
      } catch (err) {
        console.error('Duty status update error:', err.response?.data || err)
        addNotification(
          <div>Failed to update duty status. Check console for details.</div>,
          { className: 'is-danger' }
        )
      }
      setDutyStatusLoading(false)
    },
    [client, user?.id, reloadProfile, addNotification]
  )

  useEffect(() => {
    if (user?.dutyStatus !== undefined) {
      const normalizedStatus = normalizeDutyStatus(user.dutyStatus)
      setLocalUser((prev) => ({
        ...prev,
        dutyStatus: normalizedStatus ? 'on-duty' : 'off-duty',
        dutyStatusBoolean: normalizedStatus
      }))
    }
  }, [user?.dutyStatus])

  const setDefaultMapLayer = useCallback(
    (defaultMapType) => {
      setMapSettings({
        ...mapSettings,
        defaultMapType
      })
    },
    [setMapSettings, mapSettings]
  )

  const setShowTrafficLayer = useCallback(
    (showTrafficLayer) => {
      setMapSettings({
        ...mapSettings,
        showTrafficLayer
      })
    },
    [setMapSettings, mapSettings]
  )

  const setMapViewAsDefault = useCallback(() => {
    setMapSettings({
      ...mapSettings,
      defaultMapCenter: undefined,
      defaultMapZoom: undefined
    })

    addNotification(
      <div>
        <div>Current map view set as default.</div>
      </div>,
      { className: 'is-success' }
    )
  }, [setMapSettings, mapSettings, addNotification])

  const handleSignOut = async () => {
    const userId = auth.currentUser?.uid

    if (userId) {
      const userRef = doc(db, 'users', userId)

      try {
        await setDoc(
          userRef,
          {
            isOnline: false,
            lastActive: new Date().toISOString()
          },
          { merge: true }
        )
      } catch (error) {
        console.error('Error setting offline status before sign-out:', error)
      }
    }

    try {
      await signOut(auth)
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  return (
    <nav
      className={`navbar ${isDispatch ? 'is-link' : 'has-shadow'}`}
      role='navigation'
      aria-label='main navigation'>
      <div className='navbar-brand'>
        <div
          role='button'
          className='navbar-burger burger'
          onClick={() => setActive(!isActive)}
          aria-label='menu'
          aria-expanded='false'
          data-target='navbar'>
          <span aria-hidden='true'></span>
          <span aria-hidden='true'></span>
          <span aria-hidden='true'></span>
        </div>
        <span
          className={`navbar-item ${
            isDispatch ? 'has-text-white' : 'has-text-info'
          }`}
          style={{ pointerEvents: 'none', fontWeight: 800 }}>
          Live Alarm
        </span>
      </div>

      <div id='navbar' className='navbar-menu'>
        <div className='navbar-start'>
          <NavLink to='/organizations/list' match='/organizations'>
            Organizations
          </NavLink>
          <NavLink to='/sites/list' match='/sites'>
            Sites
          </NavLink>
          <NavLink to='/incidents/list' match='/incidents'>
            Incidents
          </NavLink>

          <NavLink to='/dispatch' match='/dispatch'>
            Ongoing
          </NavLink>
          {isAdmin && (
            <NavLink to='/system' match='/system'>
              System
            </NavLink>
          )}
        </div>

        <div className='navbar-end'>
          <NavLink to='/my-account'>
            <span className='icon'>
              <i className='fas fa-user-circle' />
            </span>
            <span>My Account</span>
          </NavLink>
          {user && (
            <NavLink external onClick={handleSignOut}>
              <span className='icon'>
                <i className='fas fa-sign-out-alt' />
              </span>
              <span>Log out</span>
            </NavLink>
          )}
        </div>
      </div>

      <div className={`nav-menu ${isActive ? 'is-active' : ''}`}>
        <div className='nav-menu-close'>
          <button className='button' onClick={() => setActive(false)}>
            <i className='far fa-times-circle' />
          </button>
        </div>
        <div className='nav-item'>
          <img src='/assets/images/logo.png' className='nav-logo' alt='' />
          <div className='nav-brand'>
            <div className='nav-title'>Live Alarm</div>
            <div className='nav-subtitle'>by Lifeline Applications</div>
          </div>
        </div>
        {!activeSubMenu && (
          <>
            {localUser && (
              <div className='nav-item'>
                <div className='flex flex-col gap-2'>
                  <div className='pb-3' style={{ fontWeight: 600 }}>
                    My Info:
                  </div>
                  <div>Username: {localUser?.displayName}</div>
                  {displayName && <div>Display name: {displayName.value}</div>}
                  <div className='nav-user-icon'>
                    User icon:
                    <IconPickerItem
                      icon={localUser?.userType?.icon?.stringValue || ''}
                      size={16}
                      color={localUser?.userType?.color?.stringValue || '#000'}
                    />
                  </div>
                  <div>Organization: {localUser?.organization}</div>
                  <div>User ID: {localUser?.id}</div>
                </div>
              </div>
            )}
            <div className='nav-item mt-auto'>
              <div style={{ width: '100%' }}>
                <div className='pb-4' style={{ fontWeight: 600 }}>
                  My Settings:
                </div>
                <div className='nav-setting'>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-user-check' />
                  </div>
                  <div className='nav-setting-label'>Duty Status</div>
                  <div className='nav-setting-value'>
                    <ToggleButton
                      value={normalizeDutyStatus(localUser?.dutyStatus)}
                      setValue={setDutyStatus}
                      loading={!profile || dutyStatusLoading}
                    />
                  </div>
                </div>
                <div className='nav-setting'>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-comments' />
                  </div>
                  <div className='nav-setting-label'>Chat Notifications</div>
                  <div className='nav-setting-value'>
                    <ToggleButton
                      value={chatNotifications}
                      setValue={updateChatNotifications}
                    />
                  </div>
                </div>
                <div className='nav-setting'>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-address-card' />
                  </div>
                  <div className='nav-setting-label'>Show last name on map</div>
                  <div className='nav-setting-value'>
                    <ToggleButton
                      value={mapSettings.showLastName}
                      setValue={updateShowLastName}
                    />
                  </div>
                </div>
                <div
                  className='nav-setting clickable'
                  onClick={() => setMapViewAsDefault()}>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-eye' />
                  </div>
                  <div className='nav-setting-label'>
                    Set map view as default
                  </div>
                  <div className='nav-setting-value'></div>
                </div>
                <div
                  className='nav-setting clickable'
                  onClick={() => setActiveSubMenu('map-layers')}>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-layer-group' />
                  </div>
                  <div className='nav-setting-label'>Map Layers</div>
                  <div className='nav-setting-value'></div>
                </div>
                <div
                  className='nav-setting clickable'
                  onClick={() =>
                    window.open('mailto:help@livealarm.app', '_blank').focus()
                  }>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-envelope' />
                  </div>
                  <div className='nav-setting-label'>Need help? Email us.</div>
                  <div className='nav-setting-value'></div>
                </div>
                <div
                  className='nav-setting clickable'
                  onClick={() =>
                    window
                      .open('https://www.surveymonkey.com/r/VNJRTLY', '_blank')
                      .focus()
                  }>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-comment-alt' />
                  </div>
                  <div className='nav-setting-label'>Submit feedback</div>
                  <div className='nav-setting-value'></div>
                </div>
                <div
                  className='nav-setting clickable'
                  onClick={() =>
                    window.location.replace(
                      `${baseURL}account?clientId=admin-app`
                    )
                  }>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-user-cog' />
                  </div>
                  <div className='nav-setting-label'>Account Management</div>
                  <div className='nav-setting-value'></div>
                </div>
                <div className='nav-setting clickable' onClick={handleSignOut}>
                  <div className='nav-setting-icon'>
                    <i className='fa fa-sign-out-alt' />
                  </div>
                  <div className='nav-setting-label'>Log Out</div>
                  <div className='nav-setting-value'></div>
                </div>
              </div>
            </div>
          </>
        )}
        {activeSubMenu === 'map-layers' && (
          <div style={{ marginTop: '1rem' }}>
            <div className='back-link'>
              <a href='#' onClick={() => setActiveSubMenu()}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </a>
              <div className='submenu-header'>
                <FontAwesomeIcon icon={faLayerGroup} /> Map Layers
              </div>
            </div>
            <div className='map-layer-buttons'>
              <div
                className={`map-layer-button ${
                  mapSettings.defaultMapType === 'roads' && 'active'
                }`}
                onClick={() => setDefaultMapLayer('roads')}>
                <div
                  className='image'
                  style={{ backgroundImage: `url(${mapLayerRoads})` }}
                />
                <div>Default</div>
              </div>
              <div
                className={`map-layer-button ${
                  mapSettings.defaultMapType === 'satellite' && 'active'
                }`}
                onClick={() => setDefaultMapLayer('satellite')}>
                <div
                  className='image'
                  style={{ backgroundImage: `url(${mapLayerSatellite})` }}
                />
                <div>Satellite</div>
              </div>

              <div
                className={`map-layer-button ${
                  mapSettings.defaultMapType === 'terrain' && 'active'
                }`}
                onClick={() => setDefaultMapLayer('terrain')}>
                <div
                  className='image'
                  style={{ backgroundImage: `url(${mapLayerTerrain})` }}
                />
                <div>Terrain</div>
              </div>
            </div>
            <div className='map-layer-buttons'>
              <div
                className={`map-layer-button ${
                  mapSettings.showTrafficLayer && 'active'
                } 
                  ${
                    mapSettings.defaultMapType === 'satellite' ? 'disabled' : ''
                  }`}
                onClick={() =>
                  mapSettings.defaultMapType !== 'satellite' &&
                  setShowTrafficLayer(!mapSettings.showTrafficLayer)
                }
                style={
                  mapSettings.defaultMapType === 'satellite'
                    ? { pointerEvents: 'none', opacity: 0.5 }
                    : {}
                }>
                <div
                  className='image'
                  style={{ backgroundImage: `url(${mapLayerTraffic})` }}
                />
                <div>Traffic</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
